import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { SearchX, Lock, ShieldCheck, Timer, Trash2 } from 'lucide-react';
import { API } from '../lib/constants';
import { PublicShell } from '../components/public/PublicShell';
import { WebcamCaptureStep } from '../components/public/WebcamCaptureStep';
import { RegistrationForm, SuccessCard } from '../components/public/RegistrationSteps';
import { LoadingScreen } from '../components/LoadingScreen';
import { ResendLinkCard } from '../components/public/ResendLinkCard';

const StepDots = ({ step }) => (
  <div className="flex flex-wrap items-center gap-y-3 gap-x-2 mb-6" data-testid="step-indicator">
    {['Details', 'Selfie', 'Done'].map((label, i) => {
      const n = i + 1;
      const active = step === n;
      const done = step > n;
      return (
        <div key={label} className="flex items-center gap-2">
          <span
            className={`w-7 h-7 font-mono text-[11px] font-bold flex items-center justify-center border-2 transition-colors ${
              active ? 'bg-cyan text-ink border-cyan' : done ? 'bg-lime text-ink border-lime' : 'bg-transparent text-slate-500 border-line'
            }`}
          >
            {String(n).padStart(2, '0')}
          </span>
          <span className={`font-mono text-[11px] uppercase tracking-[0.2em] ${active ? 'text-white' : 'text-slate-500'}`}>{label}</span>
          {n < 3 && <span className="hidden sm:block w-8 h-px bg-line mx-1" />}
        </div>
      );
    })}
  </div>
);

const PRIVACY_POINTS = [
  { icon: ShieldCheck, text: 'Your selfie is only the key to your album — it is never stored' },
  { icon: Timer, text: 'Signature auto-deletes 30 days after registration' },
  { icon: Trash2, text: 'Delete your data anytime from your album' },
];

const TenantRegisterPage = () => {
  const { slug } = useParams();
  const [tenant, setTenant] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({ name: '', email: '', phone: '' });
  const [capturedImage, setCapturedImage] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [regError, setRegError] = useState(null);
  const [showResend, setShowResend] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/public/tenant/${slug}`)
      .then((r) => setTenant(r.data))
      .catch(() => setNotFound(true));
  }, [slug]);

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handleRegister = async () => {
    setLoading(true);
    setRegError(null);
    try {
      const { data } = await axios.post(`${API}/register`, { ...form, face_image_data: capturedImage, tenant_slug: slug });
      setResult(data);
      setStep(3);
      toast.success('Registration successful');
    } catch (err) {
      const msg = err.response?.data?.detail || 'Registration failed. Please try again.';
      setRegError(typeof msg === 'string' ? msg : 'Registration failed. Please try again.');
      toast.error(typeof msg === 'string' ? msg : 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  if (notFound) {
    return (
      <PublicShell tenant={null}>
        <div className="card-brutal p-10 text-center max-w-md mx-auto" data-testid="tenant-not-found">
          <span className="w-12 h-12 bg-panel border-2 border-line text-slate-400 flex items-center justify-center mx-auto mb-5">
            <SearchX className="w-6 h-6" />
          </span>
          <h1 className="h-display text-2xl">Event not found</h1>
          <p className="text-sm text-slate-400 mt-2">This event link is invalid or the workspace isn't active yet.</p>
          <Link to="/" className="btn-primary mt-6">Go to homepage</Link>
        </div>
      </PublicShell>
    );
  }

  if (!tenant) return <LoadingScreen label="Loading event…" />;

  const closed = tenant.event_status === 'closed';

  return (
    <PublicShell tenant={tenant}>
      <div className="mb-8 fade-up">
        <p className="eyebrow mb-3">Your photos from this event</p>
        <h1 className="h-display text-4xl sm:text-5xl leading-[0.95]">{tenant.name}</h1>
        <p className="mt-4 text-base sm:text-lg text-slate-300 leading-relaxed max-w-2xl" data-testid="tenant-welcome-message">
          {tenant.branding.welcome_message}
        </p>
      </div>

      {closed ? (
        <div className="card p-10 text-center max-w-md mx-auto" data-testid="event-closed-card">
          <span className="w-12 h-12 bg-panel border-2 border-line text-slate-400 flex items-center justify-center mx-auto mb-5"><Lock className="w-6 h-6" /></span>
          <h2 className="h-display text-2xl">Registration closed</h2>
          <p className="text-sm text-slate-400 mt-2">This event has wrapped up and all face signatures have been purged. Existing gallery links keep working.</p>
        </div>
      ) : (
        <>
          <StepDots step={step} />
          {step === 1 && (
            <>
              <RegistrationForm form={form} onChange={onChange} onNext={() => setStep(2)} />
              <div className="mt-4 fade-up fade-up-delay-1">
                {showResend ? (
                  <ResendLinkCard slug={slug} initialEmail={form.email} />
                ) : (
                  <button data-testid="show-resend-btn" onClick={() => setShowResend(true)} className="font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400 hover:text-cyan transition-colors">
                    Already registered? Get your album link →
                  </button>
                )}
              </div>
            </>
          )}
          {step === 2 && (
            <WebcamCaptureStep
              capturedImage={capturedImage}
              onCapture={setCapturedImage}
              onRetake={() => { setCapturedImage(null); setRegError(null); }}
              onBack={() => { setStep(1); setCapturedImage(null); setRegError(null); }}
              onSubmit={handleRegister}
              loading={loading}
              error={regError}
              homePath={`/t/${slug}`}
              email={form.email}
              slug={slug}
            />
          )}
          {step === 3 && result && (
            <SuccessCard galleryId={result.gallery_id} userName={result.name} galleryPath={`/t/${slug}/gallery/${result.gallery_id}`} />
          )}
        </>
      )}

      <ul className="mt-10 grid sm:grid-cols-3 gap-3 fade-up fade-up-delay-2" data-testid="public-privacy-points">
        {PRIVACY_POINTS.map(({ icon: Icon, text }) => (
          <li key={text} className="card p-4 flex items-start gap-3">
            <Icon className="w-4 h-4 text-lime shrink-0 mt-0.5" />
            <span className="text-xs text-slate-300 leading-relaxed">{text}</span>
          </li>
        ))}
      </ul>
    </PublicShell>
  );
};

export default TenantRegisterPage;
